import { api } from '~/api';
import { useSession } from '~/features/session/session-actions';
import { analyticsBeacon, EventType } from '~/utils/analytics';

export const useUpdateUser = () => {
  const { refreshUser } = useSession();

  const updateUser = async ({ email, userInfo }) => {
    await api.users.update(email, userInfo);
    await refreshUser();
  };

  return updateUser;
};

export const useUpdateShippingAddress = () => {
  const { user, refreshUser } = useSession();

  const createShippingAddress = async (address) => {
    const createdAddress = await api.addresses.createShippingAddress({
      userUuid: user.uuid,
      email: user?.email,
      ...address,
    });

    await refreshUser();

    analyticsBeacon.emit(EventType.ACCOUNT_UPDATED, {
      userUuid: user?.uuid,
      email: user?.email,
      firstName: createdAddress?.firstName,
      lastName: createdAddress?.lastName,
      city: createdAddress?.city,
      state: createdAddress?.stateCode,
      zip: createdAddress?.postalCode,
      country: 'US',
      formattedAddress: createdAddress?.formatted,
      phoneNumber: createdAddress?.phoneNumber,
    });

    return createdAddress;
  };

  const updateShippingAddress = async (address) => {
    const updatedAddress = await api.addresses.updateShippingAddress({
      userUuid: user.uuid,
      ...address,
    });

    await refreshUser();

    analyticsBeacon.emit(EventType.ACCOUNT_UPDATED, {
      userUuid: user?.uuid,
      email: user?.email,
      firstName: updatedAddress?.firstName,
      lastName: updatedAddress?.lastName,
      city: updatedAddress?.city,
      state: updatedAddress?.stateCode,
      zip: updatedAddress?.postalCode,
      country: 'US',
      formattedAddress: updatedAddress?.formatted,
      phoneNumber: updatedAddress?.phoneNumber,
    });

    return updatedAddress;
  };

  const updateDefaultShippingAddress = async ({ uuid }) => {
    await api.addresses.updateDefaultShippingAddress({
      userUuid: user.uuid,
      addressUuid: uuid,
    });

    await refreshUser();
  };

  const deleteShippingAddress = async (address) => {
    await api.addresses.deleteShippingAddress({
      userUuid: user.uuid,
      addressUuid: address.uuid,
    });

    await refreshUser();
  };

  return {
    createShippingAddress,
    updateShippingAddress,
    updateDefaultShippingAddress,
    deleteShippingAddress,
  };
};

export const useUpdatePassword = () => {
  const { user, refreshUser } = useSession();

  const updatePassword = async (newPassword, creationMethod) => {
    await api.sessions.changePassword(newPassword);
    await refreshUser();

    analyticsBeacon.emit(EventType.PASSWORD_UPDATED, {
      creation_method: creationMethod,
      email: user?.email,
      userUuid: user?.uuid,
    });
  };

  return updatePassword;
};
